export interface PdfScheduleDay {
  code: string;
  label: string;
}

export interface PdfScheduleEntry {
  dayCode: string;
  subject: string;
  courseNumber: string;
  sectionCode?: string;
  sectionTitle?: string;
  scheduleType: string;
  room?: string;
  facultyName?: string;
  startMinutes: number;
  endMinutes: number;
  lane?: number;
  laneCount?: number;
  pinned?: boolean;
  highlighted?: boolean;
}

interface PdfScheduleOptions {
  title: string;
  subtitle?: string;
  fileName: string;
  days: PdfScheduleDay[];
  entries: PdfScheduleEntry[];
  startHour: number;
  endHour: number;
}

const pageWidth = 792;
const pageHeight = 612;
const margin = 36;
const timeColumnWidth = 52;
const dayHeaderHeight = 20;
const gridTop = 520;
const gridBottom = 48;
const gridHeight = gridTop - dayHeaderHeight - gridBottom;

const typeColors: Record<string, { fill: [number, number, number]; stroke: [number, number, number] }> = {
  LEC: { fill: [0.86, 0.92, 1], stroke: [0.58, 0.74, 0.98] },
  LAB: { fill: [0.82, 0.95, 0.88], stroke: [0.43, 0.83, 0.62] },
  TUT: { fill: [0.93, 0.87, 1], stroke: [0.76, 0.62, 0.95] },
  THS: { fill: [0.99, 0.92, 0.73], stroke: [0.98, 0.75, 0.31] },
  SEM: { fill: [0.81, 0.96, 0.98], stroke: [0.40, 0.83, 0.91] },
  WRK: { fill: [0.89, 0.91, 0.94], stroke: [0.58, 0.64, 0.72] },
  OTHER: { fill: [0.88, 0.90, 1], stroke: [0.65, 0.70, 0.96] },
};

function escapePdfText(value: string): string {
  return value
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function text(value: string, x: number, y: number, size = 8): string {
  return `BT /F1 ${size} Tf ${x.toFixed(2)} ${y.toFixed(2)} Td (${escapePdfText(value)}) Tj ET`;
}

function line(x1: number, y1: number, x2: number, y2: number): string {
  return `${x1.toFixed(2)} ${y1.toFixed(2)} m ${x2.toFixed(2)} ${y2.toFixed(2)} l S`;
}

function rect(x: number, y: number, width: number, height: number, mode: "S" | "f" | "B"): string {
  return `${x.toFixed(2)} ${y.toFixed(2)} ${width.toFixed(2)} ${height.toFixed(2)} re ${mode}`;
}

function rgb([red, green, blue]: [number, number, number], operator: "rg" | "RG"): string {
  return `${red.toFixed(3)} ${green.toFixed(3)} ${blue.toFixed(3)} ${operator}`;
}

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, Math.max(maxLength - 1, 0))}...`;
}

function formatMinutes(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function sanitizeFileName(fileName: string): string {
  const cleaned = fileName.trim().replace(/[^a-z0-9._-]+/gi, "-").replace(/-+/g, "-");
  return cleaned.toLowerCase().endsWith(".pdf") ? cleaned : `${cleaned || "schedule"}.pdf`;
}

function buildPdf(objects: string[]): string {
  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let index = 0; index < objects.length; index += 1) {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${objects[index]}\nendobj\n`;
  }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index < offsets.length; index += 1) {
    pdf += `${String(offsets[index]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return pdf;
}

function sortedEntries(entries: PdfScheduleEntry[]): PdfScheduleEntry[] {
  const dayOrder: Record<string, number> = { M: 1, T: 2, W: 3, R: 4, F: 5, S: 6, U: 7 };
  return [...entries].sort(
    (left, right) =>
      (dayOrder[left.dayCode] ?? 99) - (dayOrder[right.dayCode] ?? 99) ||
      left.startMinutes - right.startMinutes ||
      left.endMinutes - right.endMinutes ||
      `${left.subject} ${left.courseNumber} ${left.sectionCode ?? ""}`.localeCompare(
        `${right.subject} ${right.courseNumber} ${right.sectionCode ?? ""}`,
      ),
  );
}

function buildSchedulePageCommands(options: PdfScheduleOptions): string[] {
  const { title, subtitle, days, entries, startHour, endHour } = options;
  const commands: string[] = [
    "1 w",
    rgb([0.94, 0.96, 0.98], "rg"),
    rect(0, 0, pageWidth, pageHeight, "f"),
    rgb([1, 1, 1], "rg"),
    rect(margin, gridBottom, pageWidth - margin * 2, gridTop - gridBottom, "f"),
    rgb([0.10, 0.12, 0.16], "rg"),
    text(title, margin, 580, 18),
    rgb([0.39, 0.45, 0.55], "rg"),
    text(subtitle ?? `Generated ${new Date().toLocaleString()}`, margin, 562, 9),
  ];

  const dayCount = Math.max(days.length, 1);
  const gridLeft = margin + timeColumnWidth;
  const gridRight = pageWidth - margin;
  const dayWidth = (gridRight - gridLeft) / dayCount;
  const bodyTop = gridTop - dayHeaderHeight;
  const minuteStart = startHour * 60;
  const minuteEnd = endHour * 60;
  const totalMinutes = minuteEnd - minuteStart;

  commands.push(rgb([0.20, 0.25, 0.33], "RG"), rgb([0.95, 0.97, 0.99], "rg"));
  commands.push(rect(margin, bodyTop, pageWidth - margin * 2, dayHeaderHeight, "B"));
  commands.push(rgb([0.10, 0.12, 0.16], "rg"));
  commands.push(text("Time", margin + 10, bodyTop + 7, 8));

  days.forEach((day, index) => {
    const x = gridLeft + index * dayWidth;
    commands.push(line(x, gridBottom, x, gridTop));
    commands.push(rgb([0.10, 0.12, 0.16], "rg"));
    commands.push(text(day.label, x + Math.max((dayWidth - day.label.length * 4.5) / 2, 4), bodyTop + 7, 8));
  });
  commands.push(line(gridRight, gridBottom, gridRight, gridTop));

  for (let hour = startHour; hour <= endHour; hour += 1) {
    const y = bodyTop - ((hour * 60 - minuteStart) / totalMinutes) * gridHeight;
    commands.push(rgb(hour === startHour || hour === endHour ? [0.72, 0.76, 0.82] : [0.88, 0.91, 0.94], "RG"));
    commands.push(line(margin, y, gridRight, y));
    if (hour < endHour) {
      commands.push(rgb([0.39, 0.45, 0.55], "rg"));
      commands.push(text(`${String(hour).padStart(2, "0")}:00`, margin + 6, y - 11, 7));
    }
  }

  for (const entry of entries) {
    const dayIndex = days.findIndex((day) => day.code === entry.dayCode);
    if (dayIndex === -1 || entry.endMinutes <= minuteStart || entry.startMinutes >= minuteEnd) continue;

    const visibleStart = Math.max(entry.startMinutes, minuteStart);
    const visibleEnd = Math.min(entry.endMinutes, minuteEnd);
    const laneCount = Math.max(entry.laneCount ?? 1, 1);
    const lane = Math.min(entry.lane ?? 0, laneCount - 1);
    const laneWidth = dayWidth / laneCount;
    const x = gridLeft + dayIndex * dayWidth + lane * laneWidth + 2;
    const top = bodyTop - ((visibleStart - minuteStart) / totalMinutes) * gridHeight;
    const bottom = bodyTop - ((visibleEnd - minuteStart) / totalMinutes) * gridHeight;
    const height = Math.max(top - bottom, 12);
    const width = Math.max(laneWidth - 4, 18);
    const code = entry.scheduleType.trim().toUpperCase();
    const colors = typeColors[code] ?? typeColors.OTHER;
    const titleLine = `${entry.subject} ${entry.courseNumber}${entry.sectionCode ? `-${entry.sectionCode}` : ""}`;
    const sectionLine = `Sec ${entry.sectionCode ?? ""} - ${code || "OTHER"}`;
    const maxChars = Math.max(Math.floor(width / 4.5), 10);

    commands.push(rgb(colors.fill, "rg"), rgb(entry.highlighted ? [0.88, 0.11, 0.20] : colors.stroke, "RG"));
    commands.push(`${entry.highlighted || entry.pinned ? "1.6" : "0.8"} w`);
    commands.push(rect(x, bottom, width, height, "B"));
    commands.push("0.8 w");
    commands.push(rgb([0.08, 0.11, 0.17], "rg"));
    commands.push(text(truncate(titleLine, maxChars), x + 4, top - 10, 7));
    if (height >= 24) {
      commands.push(text(truncate(sectionLine, maxChars), x + 4, top - 20, 6.5));
    }
    if (height >= 36 && entry.room) {
      commands.push(text(`${formatMinutes(entry.startMinutes)}-${formatMinutes(entry.endMinutes)}`, x + 4, top - 30, 6.5));
    }
    if (height >= 48 && entry.room) {
      commands.push(text(truncate(entry.room, maxChars), x + 4, top - 40, 6.5));
    }
    if (height >= 60 && entry.facultyName) {
      commands.push(rgb([0.25, 0.30, 0.38], "rg"));
      commands.push(text(truncate(entry.facultyName, maxChars), x + 4, top - 50, 6));
    }
    if (height >= 72 && entry.sectionTitle) {
      commands.push(text(truncate(entry.sectionTitle, maxChars), x + 4, top - 60, 6));
    }
  }

  return commands;
}

function buildTablePageCommands(title: string, entries: PdfScheduleEntry[], pageIndex: number, pageCount: number): string[] {
  const columns = [
    { label: "Day", x: 44, width: 52 },
    { label: "Time", x: 98, width: 76 },
    { label: "Course / Sec", x: 176, width: 104 },
    { label: "Type", x: 282, width: 54 },
    { label: "Title", x: 338, width: 172 },
    { label: "Room", x: 512, width: 96 },
    { label: "Instructor", x: 610, width: 118 },
  ];
  const rowHeight = 16;
  const headerY = 530;
  const rowsPerPage = 27;
  const rows = entries.slice(pageIndex * rowsPerPage, (pageIndex + 1) * rowsPerPage);
  const commands = [
    "0.8 w",
    rgb([0.94, 0.96, 0.98], "rg"),
    rect(0, 0, pageWidth, pageHeight, "f"),
    rgb([0.10, 0.12, 0.16], "rg"),
    text(`${title} - Table View`, margin, 580, 16),
    rgb([0.39, 0.45, 0.55], "rg"),
    text(`Page ${pageIndex + 2} of ${pageCount + 1}`, margin, 562, 8),
    rgb([0.12, 0.18, 0.28], "rg"),
    rgb([0.12, 0.18, 0.28], "RG"),
    rect(margin, headerY, pageWidth - margin * 2, 20, "B"),
    rgb([1, 1, 1], "rg"),
    ...columns.map((column) => text(column.label, column.x, headerY + 7, 7)),
  ];

  rows.forEach((entry, index) => {
    const y = headerY - (index + 1) * rowHeight;
    const fill: [number, number, number] = index % 2 === 0 ? [1, 1, 1] : [0.98, 0.99, 1];
    const course = `${entry.subject} ${entry.courseNumber}${entry.sectionCode ? `-${entry.sectionCode}` : ""}`;
    const type = entry.scheduleType.trim().toUpperCase() || "OTHER";
    commands.push(rgb(fill, "rg"), rgb([0.88, 0.91, 0.94], "RG"));
    commands.push(rect(margin, y, pageWidth - margin * 2, rowHeight, "B"));
    commands.push(rgb([0.10, 0.12, 0.16], "rg"));
    commands.push(text(entry.dayCode, columns[0].x, y + 5, 6.5));
    commands.push(text(`${formatMinutes(entry.startMinutes)}-${formatMinutes(entry.endMinutes)}`, columns[1].x, y + 5, 6.5));
    commands.push(text(truncate(course, Math.floor(columns[2].width / 4)), columns[2].x, y + 5, 6.5));
    commands.push(text(truncate(type, 8), columns[3].x, y + 5, 6.5));
    commands.push(text(truncate(entry.sectionTitle ?? "", Math.floor(columns[4].width / 4)), columns[4].x, y + 5, 6.5));
    commands.push(text(truncate(entry.room ?? "TBA", Math.floor(columns[5].width / 4)), columns[5].x, y + 5, 6.5));
    commands.push(text(truncate(entry.facultyName ?? "TBA", Math.floor(columns[6].width / 4)), columns[6].x, y + 5, 6.5));
  });

  if (rows.length === 0) {
    commands.push(rgb([0.39, 0.45, 0.55], "rg"));
    commands.push(text("No visible timetable rows.", margin, headerY - 28, 9));
  }

  return commands;
}

function buildPagedPdf(pageCommands: string[]): string {
  const pageStreams = pageCommands.map((commands) => commands);
  const fontObjectId = 3 + pageStreams.length * 2;
  const pageObjectIds = pageStreams.map((_, index) => 3 + index * 2);
  const objects: string[] = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pageStreams.length} >>`,
  ];

  pageStreams.forEach((stream, index) => {
    const pageObjectId = pageObjectIds[index];
    const contentObjectId = pageObjectId + 1;
    objects.push(`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 792 612] /Resources << /Font << /F1 ${fontObjectId} 0 R >> >> /Contents ${contentObjectId} 0 R >>`);
    objects.push(`<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`);
  });
  objects.push("<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>");
  return buildPdf(objects);
}

export function downloadSchedulePdf(options: PdfScheduleOptions): void {
  const tableEntries = sortedEntries(options.entries);
  const rowsPerTablePage = 27;
  const tablePageCount = Math.max(Math.ceil(tableEntries.length / rowsPerTablePage), 1);
  const pageCommands = [
    buildSchedulePageCommands(options).join("\n"),
    ...Array.from({ length: tablePageCount }, (_, pageIndex) =>
      buildTablePageCommands(options.title, tableEntries, pageIndex, tablePageCount).join("\n"),
    ),
  ];
  const pdf = buildPagedPdf(pageCommands);
  const blob = new Blob([pdf], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = sanitizeFileName(options.fileName);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
